import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import theme from './theme';
import { AuthProvider, useAuth } from './store/AuthContext';
import DialogProvider from './components/DialogProvider';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import CustomerListPage from './pages/CustomerListPage';
import CustomerDetailPage from './pages/CustomerDetailPage';
import ManagementPage from './pages/ManagementPage';
import DiscoverPage from './pages/DiscoverPage';
import DomainCheckPage from './pages/DomainCheckPage';
import AutoEnrichmentPage from './pages/AutoEnrichmentPage';
import AutoEnrichmentJobDetailPage from './pages/AutoEnrichmentJobDetailPage';
import ContactDiscoveryPage from './pages/ContactDiscoveryPage';
import ContactDiscoveryJobDetailPage from './pages/ContactDiscoveryJobDetailPage';
import AutoEmailOutreachPage from './pages/AutoEmailOutreachPage';
import AutoEmailOutreachJobDetailPage from './pages/AutoEmailOutreachJobDetailPage';
import InboxPage from './pages/InboxPage';
import LeadDetailPage from './pages/LeadDetailPage';
import LeadWizardPage from './pages/LeadWizardPage';
import ProfilePage from './pages/ProfilePage';
import EngagePage from './pages/EngagePage';
import CampaignDetailPage from './pages/CampaignDetailPage';
import MarketingBrainPage from './pages/MarketingBrainPage';
import SignalPage from './pages/SignalPage';
import ProfilingTemplatePage from './pages/ProfilingTemplatePage';
import AiPromptTemplatePage from './pages/AiPromptTemplatePage';
import ContactInfoTemplatePage from './pages/ContactInfoTemplatePage';
import ContactPersonPage from './pages/ContactPersonPage';
import ContactsPage from './pages/ContactsPage';
import InteractionTemplatePage from './pages/InteractionTemplatePage';
import SystemEmailTemplatePage from './pages/SystemEmailTemplatePage';
import DemoBookingsPage from './pages/DemoBookingsPage';
import EmailCompositionPage from './pages/EmailCompositionPage';
import EmailQueuePage from './pages/EmailQueuePage';
import EmailQueueMonitorPage from './pages/EmailQueueMonitorPage';
import AutomationOverviewPage from './pages/AutomationOverviewPage';
import CalendarPage from './pages/CalendarPage';
import HomePage from './pages/HomePage';
import AiSearchPage from './pages/AiSearchPage';
import MyAccountPage from './pages/MyAccountPage';
import AnalyticsPage from './pages/AnalyticsPage';
import LogsPage from './pages/LogsPage';
import ActivityListPage from './pages/ActivityListPage';
import SegmentationPage from './pages/SegmentationPage';
import SegmentationDetailPage from './pages/SegmentationDetailPage';
import LeadsReportPage from './pages/reports/LeadsReportPage';
import IndustryReportPage from './pages/reports/IndustryReportPage';
import OutreachReportPage from './pages/reports/OutreachReportPage';
import SegmentationReportPage from './pages/reports/SegmentationReportPage';
import AiOpsReportPage from './pages/reports/AiOpsReportPage';
import MarketingReportPage from './pages/reports/MarketingReportPage';
import OverviewReportPage from './pages/reports/OverviewReportPage';
import './App.css';

function PrivateRoute({ children }) {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? children : <Navigate to="/login" />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/"
        element={
          <PrivateRoute>
            <Layout />
          </PrivateRoute>
        }
      >
        <Route index element={<HomePage />} />
        <Route path="contacts" element={<ContactsPage />} />
        <Route path="customers" element={<CustomerListPage />} />
        <Route path="customers/:id" element={<CustomerDetailPage />} />
        <Route path="customers/:parentId/contacts/new" element={<ContactPersonPage />} />
        <Route path="customers/:parentId/contacts/:contactId" element={<ContactPersonPage />} />
        <Route path="activities" element={<ActivityListPage />} />
        <Route path="leads" element={<DiscoverPage />} />
        <Route path="leads/domain-check" element={<DomainCheckPage />} />
        <Route path="leads/auto-enrichment" element={<AutoEnrichmentPage />} />
        <Route path="leads/auto-enrichment/:id" element={<AutoEnrichmentJobDetailPage />} />
        <Route path="leads/contact-discovery" element={<ContactDiscoveryPage />} />
        <Route path="leads/contact-discovery/:id" element={<ContactDiscoveryJobDetailPage />} />
        <Route path="leads/auto-outreach" element={<AutoEmailOutreachPage />} />
        <Route path="leads/auto-outreach/:id" element={<AutoEmailOutreachJobDetailPage />} />
        <Route path="outreach/inbox" element={<InboxPage />} />
        <Route path="ai-search" element={<AiSearchPage />} />
        <Route path="leads/new" element={<LeadWizardPage />} />
        <Route path="leads/:id/edit" element={<LeadWizardPage />} />
        <Route path="leads/:id" element={<LeadDetailPage />} />
        <Route path="leads/:parentId/contacts/new" element={<ContactPersonPage />} />
        <Route path="leads/:parentId/contacts/:contactId" element={<ContactPersonPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="engage" element={<EngagePage />} />
        <Route path="engage/campaigns/:id" element={<CampaignDetailPage />} />
        <Route path="workspace" element={<Navigate to="/" replace />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="management" element={<ManagementPage />} />
        <Route path="management/marketing-brain/new" element={<MarketingBrainPage />} />
        <Route path="management/marketing-brain/:id" element={<MarketingBrainPage />} />
        <Route path="management/signals/new" element={<SignalPage />} />
        <Route path="management/signals/:id" element={<SignalPage />} />
        <Route path="management/profiling-templates/new" element={<ProfilingTemplatePage />} />
        <Route path="management/profiling-templates/:id" element={<ProfilingTemplatePage />} />
        <Route path="management/ai-prompts/new" element={<AiPromptTemplatePage />} />
        <Route path="management/ai-prompts/:id" element={<AiPromptTemplatePage />} />
        <Route path="management/field-templates/new" element={<ContactInfoTemplatePage />} />
        <Route path="management/field-templates/:id" element={<ContactInfoTemplatePage />} />
        <Route path="management/interaction-templates/new" element={<InteractionTemplatePage />} />
        <Route path="management/interaction-templates/:id" element={<InteractionTemplatePage />} />
        <Route path="management/email-templates/:key" element={<SystemEmailTemplatePage />} />
        <Route path="management/demo-bookings" element={<DemoBookingsPage />} />
        <Route path="management/logs" element={<LogsPage />} />
        <Route path="email-compose/:leadId" element={<EmailCompositionPage />} />
        <Route path="email-queue" element={<EmailQueuePage />} />
        <Route path="email-queue/monitor" element={<EmailQueueMonitorPage />} />
        <Route path="automation" element={<AutomationOverviewPage />} />
        <Route path="my-account" element={<MyAccountPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="segmentation" element={<SegmentationPage />} />
        <Route path="segmentation/new" element={<SegmentationDetailPage />} />
        <Route path="segmentation/:id" element={<SegmentationDetailPage />} />
        <Route path="reports" element={<OverviewReportPage />} />
        <Route path="reports/overview" element={<OverviewReportPage />} />
        <Route path="reports/leads" element={<LeadsReportPage />} />
        <Route path="reports/industries" element={<IndustryReportPage />} />
        <Route path="reports/outreach" element={<OutreachReportPage />} />
        <Route path="reports/segmentations" element={<SegmentationReportPage />} />
        <Route path="reports/ai-ops" element={<AiOpsReportPage />} />
        <Route path="reports/marketing" element={<MarketingReportPage />} />

      </Route>
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <DialogProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </AuthProvider>
      </DialogProvider>
    </ThemeProvider>
  );
}
